/**
 * TALK RATE KIND — plan-017 CLS (design §29.5, §29.17 IC-PR-1; ADR 0054, proposed; glossary §6.28).
 *
 * What a billed part of a conversation is priced as. Six values, one per price an organization pays (owner answers Q3
 * + Q5: browser voice and web chat text are inbound only; Q6: a forwarded part is the outbound kind of the same leg).
 * The Postgres type `talk_rate_kind` (migration 0137) carries the same labels in the same order; DAY's agreement suite
 * (`packages/db/test/talk-rate-kind-agreement.pg.test.ts`) proves the SQL classifier and the TypeScript one
 * (`talkRateKindOf`, `@wetalk/payments`) agree on every cell.
 *
 * Customer copy (UI copy, never an identifier; the portal catalogue carries the Greek):
 *
 * | Value                  | Copy                                |
 * |------------------------|-------------------------------------|
 * | `carrier_leg_inbound`  | Calls to your WeTalk number         |
 * | `carrier_leg_outbound` | Calls from your WeTalk number       |
 * | `sip_trunk_inbound`    | Calls on your own SIP trunk         |
 * | `sip_trunk_outbound`   | Calls out over your own SIP trunk   |
 * | `browser`              | Website voice                       |
 * | `text`                 | Web chat                            |
 *
 * Also here, because `packages/payments` reads JSON through it and `@wetalk/domain` is where zod lives: the wire shape
 * of one `conversation.talk_rate_pin` entry (design §29.3.6), the TypeScript twin of 0137's
 * `talk_rate_pin_shape_valid(jsonb)`. `talkRatePinOf` (`@wetalk/payments`) is the only reader; nothing else parses a
 * pin.
 */
import { z } from "zod";
/** `CREATE TYPE talk_rate_kind AS ENUM (…)` — migration 0137, the same labels in the same order. */
export const TALK_RATE_KIND = [
    "carrier_leg_inbound",
    "carrier_leg_outbound",
    "sip_trunk_inbound",
    "sip_trunk_outbound",
    "browser",
    "text",
];
/** Parses one `talk_rate_kind` label; refuses anything else. */
export const talkRateKindCodec = z.enum(TALK_RATE_KIND);
/**
 * Where a resolved talk rate came from: the organization's own rate, or the standard (owner answer Q2) — and, since
 * plan-020 DOM (design §32.2.4, §32.22.2; ADR 0069, proposed; added 2026-10-16), `offer`: the offer talk rate of the
 * revision named by the conversation's pinned billing assignment (`commercial_revision_talk_rate`, glossary §6.35). The
 * resolver reads them own → offer → standard per kind (OD-3); an `offer` pin entry's `talk_rate_id` is the
 * `commercial_revision_id`. Appended last: a stored `standard` or `account` pin reads unchanged.
 */
export const TALK_RATE_SOURCE = ["standard", "account", "offer"];
/** The pin's sources, as a set: {@link talkRateSourceOf}'s membership test. */
const TALK_RATE_SOURCE_SET = new Set(TALK_RATE_SOURCE);
/**
 * Reads one talk rate source, throwing on anything else (design §32.20: "an unknown pin source throws"). Never a default:
 * a value outside {@link TALK_RATE_SOURCE} is a defect in whatever wrote it, not a standard rate.
 */
export function talkRateSourceOf(value) {
    if (typeof value !== "string" || !TALK_RATE_SOURCE_SET.has(value)) {
        throw new RangeError(`${JSON.stringify(value)} is not a talk rate source (one of ${TALK_RATE_SOURCE.join(", ")})`);
    }
    return value;
}
/** Any UUID, as Postgres' `uuid` input accepts it (lower or upper hex, hyphenated) — the pin records the row's id. */
const UUID_TEXT = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
/**
 * One kind's entry in `conversation.talk_rate_pin`, exactly the five keys 0137's shape check requires: a non-negative
 * whole number of minor units a minute (PQ-13, 0 allowed), `half_up`, `EUR`, the source and the row's id. Strict: an
 * unknown key is refused, never ignored.
 */
export const TalkRatePinEntrySchema = z.strictObject({
    talk_rate_minor_per_minute: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER),
    rounding: z.literal("half_up"),
    currency: z.literal("EUR"),
    source: z.enum(TALK_RATE_SOURCE),
    talk_rate_id: z.string().regex(UUID_TEXT),
});
/**
 * The whole `conversation.talk_rate_pin` JSONB: keys are `talk_rate_kind` labels (an unknown key is refused), every
 * kind that resolved at the pin instant present (in practice all six). Never empty — `talk_rate_pin_of` returns NULL,
 * not `{}`, when no kind resolves, so an empty object is refused as a defect.
 */
export const TalkRatePinJsonSchema = z
    .partialRecord(z.enum(TALK_RATE_KIND), TalkRatePinEntrySchema)
    .refine((pin) => Object.keys(pin).length > 0, { message: "a talk rate pin names at least one talk rate kind" });
//# sourceMappingURL=talk-rate.js.map